/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {ExclamationTriangleIcon} from '@heroicons/react/20/solid';
import type {AxiosError} from 'axios';
import {toast} from 'react-toastify';
import useLocales from '@/hooks/useLocales';
import useError from '@/hooks/useError';
import LoadingScreen from '@/components/common/LoadingScreen';
import {
  createFormFields,
  getFormFields,
  updateFormFields
} from '@/apis/FormFields';
import {
  createPositionFields,
  getPositionFields,
  updatePositionFields
} from '@/apis/PositionFields';
import {makeOptionKey} from './NewChoiceFollowUpField';

type Requirement = 'OFF' | 'OPTIONAL' | 'REQUIRED';

type PartyConfig = {
  key: string;
  label: string;
  is_active: boolean;
  saved?: boolean;
};

const REQUIREMENTS: Requirement[] = ['OFF', 'OPTIONAL', 'REQUIRED'];
const PARTS = [
  'action_parties',
  'comment',
  'photos',
  'documents',
  'decision'
] as const;
type Part = (typeof PARTS)[number];
const DOCUMENT_TYPES = ['PDF', 'WORD', 'EXCEL', 'POWERPOINT', 'JPEG', 'PNG'];

export const DEFAULT_PARTIES: PartyConfig[] = [
  {key: 'company', label: 'Bedrijf', is_active: true},
  {key: 'client', label: 'Klant', is_active: true}
];

const DEFAULT_REQUIREMENTS: Record<Part, Requirement> = {
  action_parties: 'OPTIONAL',
  comment: 'OPTIONAL',
  photos: 'OPTIONAL',
  documents: 'OFF',
  decision: 'REQUIRED'
};

const notDeleted = {
  is_deleted: false
};

/**
 * Settings of an 'Afwijking vastleggen' field (field type deviation).
 *
 * The administrator chooses the action parties (with stable keys), which parts
 * are off, optional or required, the texts of the decision, the file rules,
 * whether a stop blocks completing the step, and optionally an assessment
 * result field whose new results ask to reassess the decision.
 */
const NewDeviationField = (props: any) => {
  const {translate} = useLocales();
  const {handleErrors} = useError();
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [parties, setParties] = React.useState<PartyConfig[]>(DEFAULT_PARTIES);
  const [allowMultiple, setAllowMultiple] = React.useState(true);
  const [requirements, setRequirements] =
    React.useState<Record<Part, Requirement>>(DEFAULT_REQUIREMENTS);
  const [question, setQuestion] = React.useState('');
  const [continueLabel, setContinueLabel] = React.useState('');
  const [stopLabel, setStopLabel] = React.useState('');
  const [stopBlocksStep, setStopBlocksStep] = React.useState(false);
  const [contextSource, setContextSource] = React.useState<number | ''>('');
  const [appsMayRead, setAppsMayRead] = React.useState(true);
  const [appsMayDeliver, setAppsMayDeliver] = React.useState(true);
  const [websiteMayEdit, setWebsiteMayEdit] = React.useState(true);
  const [documentTypes, setDocumentTypes] = React.useState<string[]>(['PDF']);
  const [maxFileSize, setMaxFileSize] = React.useState(10);
  const [maxPhotos, setMaxPhotos] = React.useState(10);
  const [maxDocuments, setMaxDocuments] = React.useState(10);
  const [sources, setSources] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const isPositionField =
    props?.type === 'position_field' ||
    window.location.pathname.includes('new_position_field');

  React.useEffect(() => {
    // assessment result fields of the same kind can be the source
    const request = isPositionField
      ? getPositionFields({filter: notDeleted, page_size: -1})
      : getFormFields({filters: notDeleted, page_size: -1});
    request
      .then((response: any) =>
        setSources(
          (response.data.results ?? []).filter(
            (f: any) => f.field_type === 'assessment_result'
          )
        )
      )
      .catch(() => setSources([]));
  }, []);

  React.useEffect(() => {
    if (props?.id) {
      setLabel(props.label ?? '');
      setDescription(props.description ?? '');
      setParties(
        (props.action_parties ?? []).map((p: PartyConfig) => ({
          ...p,
          saved: true
        }))
      );
      setAllowMultiple(props.allow_multiple_parties ?? true);
      setRequirements(
        Object.fromEntries(
          PARTS.map(part => [
            part,
            props[`${part}_requirement`] ?? DEFAULT_REQUIREMENTS[part]
          ])
        ) as Record<Part, Requirement>
      );
      setQuestion(props.decision_question ?? '');
      setContinueLabel(props.continue_label ?? '');
      setStopLabel(props.stop_label ?? '');
      setStopBlocksStep(Boolean(props.stop_blocks_step));
      setContextSource(props.context_source ?? '');
      setAppsMayRead(props.apps_may_read ?? true);
      setAppsMayDeliver(props.apps_may_deliver ?? true);
      setWebsiteMayEdit(props.website_may_edit ?? true);
      setDocumentTypes(props.document_types ?? []);
      setMaxFileSize(props.max_file_size ?? 10);
      setMaxPhotos(props.max_photos ?? 10);
      setMaxDocuments(props.max_documents ?? 10);
    } else {
      setLabel('');
      setDescription('');
      setParties(DEFAULT_PARTIES);
      setRequirements(DEFAULT_REQUIREMENTS);
    }
  }, [props?.id]);

  const updateParty = (index: number, changes: Partial<PartyConfig>) =>
    setParties(prev =>
      prev.map((party, i) => (i === index ? {...party, ...changes} : party))
    );

  const documentsUsed = requirements.documents !== 'OFF';
  const stopWithoutDecision = stopBlocksStep && requirements.decision === 'OFF';
  const invalid =
    !label.trim() ||
    parties.filter(p => p.is_active).length === 0 ||
    parties.some(p => !p.label.trim()) ||
    (documentsUsed && documentTypes.length === 0) ||
    stopWithoutDecision;

  const submitField = () => {
    setSubmitted(true);
    props?.setRestoreFieldName?.('');
    if (invalid) {
      return;
    }
    const keys: string[] = [];
    const payloadParties = parties.map(party => {
      const key = party.key || makeOptionKey(party.label, keys);
      keys.push(key);
      const {saved, ...rest} = party;
      return {...rest, key};
    });
    const payload: any = {
      field_type: 'deviation',
      label,
      description,
      placeholder: '',
      action_parties: payloadParties,
      allow_multiple_parties: allowMultiple,
      ...Object.fromEntries(
        PARTS.map(part => [`${part}_requirement`, requirements[part]])
      ),
      decision_question: question,
      continue_label: continueLabel,
      stop_label: stopLabel,
      stop_blocks_step: stopBlocksStep,
      context_source: contextSource === '' ? null : contextSource,
      apps_may_read: appsMayRead,
      apps_may_deliver: appsMayDeliver,
      website_may_edit: websiteMayEdit,
      document_types: documentTypes,
      max_file_size: maxFileSize,
      max_photos: maxPhotos,
      max_documents: maxDocuments
    };
    const request = props?.id
      ? (isPositionField ? updatePositionFields : updateFormFields)({
          ...payload,
          id: props.id
        })
      : (isPositionField ? createPositionFields : createFormFields)(payload);

    setLoading(true);
    request
      .then(() => {
        setLoading(false);
        setSubmitted(false);
        props?.setUpdated?.(true);
        toast.success(translate('submitted_success'));
      })
      .catch((error: AxiosError<any>) => {
        setLoading(false);
        if (
          error?.response?.data?.non_field_errors?.[0] ===
          'deleted_field_with_provided_label_already_exists'
        ) {
          props?.setRestoreFieldName?.(label);
          toast.error(translate('field_already_exist'));
        } else {
          handleErrors(error?.response?.data);
        }
      });
  };

  const checkbox = (
    checked: boolean,
    onChange: (checked: boolean) => void,
    text: string,
    testId?: string
  ) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          inputProps={{'data-testid': testId} as any}
          onChange={event => onChange(event.target.checked)}
        />
      }
      label={text}
    />
  );

  return (
    <div className="flex flex-col justify-between min-h-[70vh] gap-6">
      <div className="mt-5 flex flex-col gap-5 overflow-y-auto">
        <FormControl className="w-[300px]">
          <FormLabel className="text-[#212B36] mb-1">
            {translate('Label')}
          </FormLabel>
          <TextField
            size="small"
            value={label}
            inputProps={{'data-testid': 'deviation-field-label'}}
            onChange={event => setLabel(event.target.value)}
            placeholder={translate('Label')}
            error={submitted && !label.trim()}
            helperText={
              submitted && !label.trim() ? translate('required') : null
            }
          />
        </FormControl>
        <FormControl className="w-full max-w-[600px]">
          <FormLabel className="text-[#212B36] mb-1">
            {translate('follow_up_instruction')}
          </FormLabel>
          <TextField
            size="small"
            multiline
            minRows={2}
            value={description}
            onChange={event => setDescription(event.target.value)}
          />
        </FormControl>

        <div>
          <p className="text-[16px] mb-2">{translate('deviation_parts')}</p>
          <p className="text-[12px] text-gray-500 mb-3">
            {translate('deviation_parts_help')}
          </p>
          <div className="flex flex-wrap gap-4">
            {PARTS.map(part => (
              <div key={part} className="flex items-center gap-2">
                <span className="text-sm">
                  {translate(`deviation_part_${part}`)}
                </span>
                <Select
                  size="small"
                  value={requirements[part]}
                  className="bg-white min-w-[120px]"
                  data-testid={`deviation-requirement-${part}`}
                  onChange={event =>
                    setRequirements(prev => ({
                      ...prev,
                      [part]: event.target.value as Requirement
                    }))
                  }
                >
                  {REQUIREMENTS.map(requirement => (
                    <MenuItem key={requirement} value={requirement}>
                      {translate(`follow_up_${requirement.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[16px] mb-2">
            {translate('deviation_action_parties')}
          </p>
          <div className="flex flex-col gap-2">
            {parties.map((party, index) => (
              <div
                key={party.key || `new-${index}`}
                data-testid="deviation-party-config"
                className={`flex flex-wrap items-center gap-3 ${
                  party.is_active ? '' : 'opacity-60'
                }`}
              >
                <TextField
                  size="small"
                  label={translate('follow_up_option_label')}
                  value={party.label}
                  onChange={event =>
                    updateParty(index, {label: event.target.value})
                  }
                  error={submitted && !party.label.trim()}
                />
                {party.saved ? (
                  <>
                    {checkbox(
                      party.is_active,
                      checked => updateParty(index, {is_active: checked}),
                      translate('follow_up_option_active')
                    )}
                    <span className="text-[11px] text-gray-400">
                      {translate('follow_up_option_key')}: {party.key}
                    </span>
                  </>
                ) : (
                  <IconButton
                    aria-label={translate('follow_up_remove_option')}
                    onClick={() =>
                      setParties(prev => prev.filter((_, i) => i !== index))
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
          <Button
            onClick={() =>
              setParties(prev => [
                ...prev,
                {key: '', label: '', is_active: true}
              ])
            }
            className="font-open-sans normal-case underline pl-0 mt-2"
          >
            + {translate('deviation_add_party')}
          </Button>
          <div>
            {checkbox(
              allowMultiple,
              setAllowMultiple,
              translate('deviation_allow_multiple_parties')
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 max-w-[600px]">
          <p className="text-[16px]">{translate('deviation_decision')}</p>
          <TextField
            size="small"
            label={translate('deviation_decision_question_label')}
            placeholder={translate('deviation_decision_question')}
            value={question}
            onChange={event => setQuestion(event.target.value)}
          />
          <div className="flex flex-wrap gap-3">
            <TextField
              size="small"
              className="min-w-[260px]"
              label={translate('deviation_continue_label')}
              placeholder={translate('deviation_continue')}
              value={continueLabel}
              onChange={event => setContinueLabel(event.target.value)}
            />
            <TextField
              size="small"
              className="min-w-[260px]"
              label={translate('deviation_stop_label')}
              placeholder={translate('deviation_stop')}
              value={stopLabel}
              onChange={event => setStopLabel(event.target.value)}
            />
          </div>
          <div>
            {checkbox(
              stopBlocksStep,
              setStopBlocksStep,
              translate('deviation_stop_blocks_step'),
              'deviation-stop-blocks'
            )}
            <p className="text-[12px] text-gray-500">
              {translate('deviation_stop_blocks_step_help')}
            </p>
            {submitted && stopWithoutDecision && (
              <span className="text-error text-sm">
                {translate('deviation_stop_blocks_without_decision')}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 max-w-[600px]">
          <p className="text-[16px]">{translate('deviation_context_source')}</p>
          <p className="text-[12px] text-gray-500">
            {translate('deviation_context_source_help')}
          </p>
          <Select
            size="small"
            displayEmpty
            value={contextSource}
            className="bg-white"
            data-testid="deviation-context-source"
            onChange={event =>
              setContextSource(
                event.target.value === '' ? '' : Number(event.target.value)
              )
            }
          >
            <MenuItem value="">
              {translate('deviation_context_manual')}
            </MenuItem>
            {sources.map(source => (
              <MenuItem key={source.id} value={source.id}>
                {source.label}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="flex flex-col">
          <p className="text-[16px]">{translate('deviation_apps')}</p>
          {checkbox(
            appsMayRead,
            setAppsMayRead,
            translate('deviation_apps_may_read')
          )}
          {checkbox(
            appsMayDeliver,
            setAppsMayDeliver,
            translate('deviation_apps_may_deliver')
          )}
          {checkbox(
            websiteMayEdit,
            setWebsiteMayEdit,
            translate('deviation_website_may_edit')
          )}
          <p className="text-[12px] text-gray-500">
            {translate('deviation_website_may_edit_help')}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-[16px]">{translate('follow_up_file_rules')}</p>
          <div>
            <p className="text-sm mb-1">
              {translate('follow_up_document_types')}
            </p>
            <div className="flex flex-wrap">
              {DOCUMENT_TYPES.map(type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={documentTypes.includes(type)}
                      onChange={event =>
                        setDocumentTypes(prev =>
                          event.target.checked
                            ? [...prev, type]
                            : prev.filter(t => t !== type)
                        )
                      }
                    />
                  }
                  label={type}
                />
              ))}
            </div>
            {submitted && documentsUsed && documentTypes.length === 0 && (
              <span className="text-error text-sm">
                {translate('follow_up_document_types_required')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <TextField
              size="small"
              type="number"
              label={translate('follow_up_max_file_size')}
              value={maxFileSize}
              inputProps={{min: 1}}
              onChange={event => setMaxFileSize(Number(event.target.value))}
            />
            <TextField
              size="small"
              type="number"
              label={translate('follow_up_max_photos')}
              value={maxPhotos}
              inputProps={{min: 1}}
              onChange={event => setMaxPhotos(Number(event.target.value))}
            />
            <TextField
              size="small"
              type="number"
              label={translate('follow_up_max_documents')}
              value={maxDocuments}
              inputProps={{min: 1}}
              onChange={event => setMaxDocuments(Number(event.target.value))}
            />
          </div>
        </div>
      </div>
      {loading && <LoadingScreen />}
      <div className="flex">
        {props?.is_standard && (
          <div className="flex">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-dark-yellow"
              aria-hidden="true"
            />
            <span className="text-dark-yellow">
              {translate('not_editable')}
            </span>
          </div>
        )}
        <Button
          variant="contained"
          size="small"
          color="inherit"
          data-testid="deviation-field-save"
          className="bg-[#144A6C] mr-5 px-7 h-[20px] text-white normal-case"
          onClick={submitField}
          disabled={loading}
        >
          {translate('save')}
        </Button>
      </div>
    </div>
  );
};

export default NewDeviationField;
